import {Checkbox, Col, Radio} from "antd"
import styles from "./DailyAttendance.module.css"
import {useCallback, useEffect, useState} from "react"
import RoundingRange from "./RoundingRange"
import {BaseButton, BaseInput, BaseLoading} from "components"
import {useTranslation} from "react-i18next"
import {
    AccommodatedHoursOption,
    AttendanceRoundingItem,
    AttendanceRoundingType,
    DailyAttendanceBreakOption,
    DailyAttendanceTimeClockTracking,
    GeneralSettingField
} from "types/settings/general-setting"
import {handleError, toastError, toastSuccess} from "helpers"
import {cloneDeep, isEqual, pick} from "lodash"
import {useAttendanceRounding, useDailyAttendanceSettings} from "hooks"
import {useMutation} from "@tanstack/react-query"
import {generalSettingsService} from "services"
import {YesNo} from "types/common"
import RoundingTables from "./RoundingTables"
import {v4 as uuid} from "uuid"

type DailyAttendanceProps = {}
export enum RoundingType {
    NoRounding = "NoRounding",
    Rounding = "Rounding"
}
const DailyAttendance = (props: DailyAttendanceProps) => {
    const [isRoundingEnabled, setIsRoundingEnabled] = useState(false)
    const {t} = useTranslation(["common"])
    const settings = useDailyAttendanceSettings()
    const [dailyAttendanceTimeClockTracking, setDailyAttendanceTimeClockTracking] = useState(
        DailyAttendanceTimeClockTracking.AnyTime
    )
    const [dailyAttendanceBreakOption, setDailyAttendanceBreakOption] = useState(DailyAttendanceBreakOption.AnyTime)
    const [enableDocumentAttachment, setEnableDocumentAttachment] = useState(false)
    const [enableDocumentAttachmentMandatory, setEnableDocumentAttachmentMandatory] = useState(false)
    const [enableRemoteMakeupAttendance, setEnableRemoteMakeupAttendance] = useState(false)
    const [enableEbookLearningRemote, setEnableEbookLearningRemote] = useState(false)
    const [enableDailySpecialRoundingForCheckin, setEnableDailySpecialRoundingForCheckin] = useState(false)
    const [enableAttendanceCheckBreakEvent, setEnableAttendanceCheckBreakEvent] = useState(false)
    const [enableLateAttendanceApproval, setEnableLateAttendanceApproval] = useState(false)
    const [baseOnScheduleCheckinBufferMinutesEnabled, setBaseOnScheduleCheckinBufferMinutesEnabled] = useState(false)
    const [baseOnScheduleCheckoutBufferMinutesEnabled, setBaseOnScheduleCheckoutBufferMinutesEnabled] = useState(false)
    const [baseOnScheduleHourPlusCheckinBufferMinutes, setBaseOnScheduleHourPlusCheckinBufferMinutes] =
        useState<string>("0")
    const [baseOnScheduleHourPlusCheckoutBufferMinutes, setBaseOnScheduleHourPlusCheckoutBufferMinutes] =
        useState<string>("0")
    const [dailyAttendanceBreakMinutes, setDailyAttendanceBreakMinutes] = useState<string>("0")
    const [lateAttendanceApprovalMinutes, setLateAttendanceApprovalMinutes] = useState<string>("0")
    const [lateAttendanceApprovalMessages, setLateAttendanceApprovalMessages] = useState<string>("")
    const [enableRejectionOfDailyAttendanceEnabled, setEnableRejectionOfDailyAttendanceEnabled] = useState(false)
    const [rejectionOfDailyAttendanceDays, setRejectionOfDailyAttendanceDays] = useState<string>("0")
    const [rejectionOfDailyAttendanceMessages, setRejectionOfDailyAttendanceMessages] = useState<string>("")
    const [enableLockDailyAttendance, setEnableLockDailyAttendance] = useState(false)
    const [lockDailyAttendanceDays, setLockDailyAttendanceDays] = useState<string>("0")
    const [lockDailyAttendanceMessages, setLockDailyAttendanceMessages] = useState<string>("")
    const [attendanceRoundingTables, setAttendanceRoundingTables] = useState([])
    const [specialAttendanceRoundingRanges, setSpecialAttendanceRoundingRanges] = useState([])
    const [accommodatedHoursOption, setAccommodatedHoursOption] = useState(AccommodatedHoursOption.AnyTime)

    const attendanceRounding = useAttendanceRounding({
        type: AttendanceRoundingType.Daily
    })

    const updateGeneralSettings = useMutation(
        async (payload: Array<{field: GeneralSettingField; checked: boolean | string}>) => {
            await generalSettingsService.updateSettings(
                payload.map(({field, checked}) => {
                    if (typeof checked === "boolean") {
                        return {
                            field,
                            value: checked ? YesNo.Yes : YesNo.No
                        }
                    }
                    return {
                        field,
                        value: checked
                    }
                })
            )
        },
        {
            onSuccess() {
                settings.refetch()
            },
            onError: (err) => {
                handleError(err)
            }
        }
    )

    useEffect(() => {
        setEnableDocumentAttachment(settings.isMakeupDocumentAttachmentEnabled)
        setEnableRemoteMakeupAttendance(settings.isRemoteMakeupDocumentEnabled)
        setEnableEbookLearningRemote(settings.isEbookLearningRemoteEnabled)
        setEnableDocumentAttachmentMandatory(settings.isMakeupDocumentAttachmentMandatory)
        setEnableDailySpecialRoundingForCheckin(attendanceRounding.specialAttendanceRoundingEnabled)
        setDailyAttendanceTimeClockTracking(settings.dailyAttendanceTimeClockTracking)
        setDailyAttendanceBreakOption(settings.dailyAttendanceBreakOption)
        setDailyAttendanceBreakMinutes(settings.dailyAttendanceBreakMinutes)
        setEnableAttendanceCheckBreakEvent(settings.isAttendanceCheckBreakEvent)
        setEnableLateAttendanceApproval(settings.isLateAttendanceApproval)
        setLateAttendanceApprovalMinutes(settings.lateAttendanceApprovalMinutes)
        setLateAttendanceApprovalMessages(settings.lateAttendanceApprovalMessages)
        setEnableRejectionOfDailyAttendanceEnabled(settings.isRejectionOfDailyAttendanceEnabled)
        setRejectionOfDailyAttendanceDays(settings.rejectionOfDailyAttendanceDays)
        setRejectionOfDailyAttendanceMessages(settings.rejectionOfDailyAttendanceMessages)
        setEnableLockDailyAttendance(settings.isLockDailyAttendanceEnabled)
        setLockDailyAttendanceDays(settings.lockDailyAttendanceDays)
        setLockDailyAttendanceMessages(settings.lockDailyAttendanceMessages)
        setAccommodatedHoursOption(settings.accommodatedHoursOption)
        setBaseOnScheduleHourPlusCheckinBufferMinutes(settings.baseOnScheduleHourPlusCheckinBufferMinutes)
        setBaseOnScheduleHourPlusCheckoutBufferMinutes(settings.baseOnScheduleHourPlusCheckoutBufferMinutes)
        setBaseOnScheduleCheckinBufferMinutesEnabled(settings.baseOnScheduleCheckinBufferMinutesEnabled)
        setBaseOnScheduleCheckoutBufferMinutesEnabled(settings.baseOnScheduleCheckoutBufferMinutesEnabled)
    }, [
        settings.isMakeupDocumentAttachmentEnabled,
        settings.isRemoteMakeupDocumentEnabled,
        settings.isEbookLearningRemoteEnabled,
        settings.isMakeupDocumentAttachmentMandatory,
        settings.dailyAttendanceTimeClockTracking,
        attendanceRounding.specialAttendanceRoundingEnabled,
        settings.isAttendanceCheckBreakEvent,
        settings.accommodatedHoursOption,
        settings.isLateAttendanceApproval,
        settings.lateAttendanceApprovalMinutes,
        settings.lateAttendanceApprovalMessages,
        settings.isRejectionOfDailyAttendanceEnabled,
        settings.rejectionOfDailyAttendanceDays,
        settings.rejectionOfDailyAttendanceMessages,
        settings.isLockDailyAttendanceEnabled,
        settings.lockDailyAttendanceDays,
        settings.lockDailyAttendanceMessages,
        settings.baseOnScheduleHourPlusCheckinBufferMinutes,
        settings.baseOnScheduleHourPlusCheckoutBufferMinutes,
        settings.baseOnScheduleCheckinBufferMinutesEnabled,
        settings.baseOnScheduleCheckoutBufferMinutesEnabled,
        settings.dailyAttendanceBreakOption,
        settings.dailyAttendanceBreakMinutes
    ])

    useEffect(() => {
        setIsRoundingEnabled(attendanceRounding.isEnabled)
    }, [attendanceRounding.isEnabled])

    useEffect(() => {
        setAttendanceRoundingTables(
            cloneDeep(
                attendanceRounding.attendanceRoundingTables.map((item, index) => ({...item, id: item.roundingTableId}))
            )
        )
        setSpecialAttendanceRoundingRanges(
            cloneDeep(attendanceRounding.specialAttendanceRoundingRanges.map((item, index) => ({...item, id: index})))
        )
    }, [attendanceRounding.attendanceRoundingTables, attendanceRounding.specialAttendanceRoundingRanges])

    const validateAttendanceRoundingItem = (attendanceRoundingRanges: AttendanceRoundingItem[]) => {
        for (let attendanceRounding of attendanceRoundingRanges) {
            if (
                !attendanceRounding.from?.toString()?.length ||
                !attendanceRounding.to?.toString()?.length ||
                !attendanceRounding.value?.toString()?.length
            ) {
                return "From, To, Value cannot be empty."
            }
            if (Number(attendanceRounding.from) > Number(attendanceRounding.to)) {
                return "From must be greater than or equal To"
            }
        }
        return false
    }

    const addNewAttendanceRoundingTable = () => {
        const newAttendanceRoundingTable = {
            roundingTableId: uuid(),
            isAppliedAllCampus: true,
            campuses: [],
            attendanceRoundingRanges: []
        }

        setAttendanceRoundingTables([...attendanceRoundingTables, newAttendanceRoundingTable])
    }

    const removeAttendanceRoundingTable = (roundingTableId: number | string) => {
        setAttendanceRoundingTables(
            attendanceRoundingTables.filter((table) => table.roundingTableId !== roundingTableId)
        )
    }

    const setAttendanceRoundingTable = (payload: {
        roundingTableId: number | string
        attendanceRoundingRanges: any[]
        isAppliedAllCampus: boolean
        campuses: any[]
    }) => {
        const {roundingTableId, attendanceRoundingRanges, isAppliedAllCampus, campuses} = payload

        const newAttendanceRoundingTables = cloneDeep(attendanceRoundingTables).map((item) => {
            if (item.roundingTableId === roundingTableId) {
                item.attendanceRoundingRanges = cloneDeep(attendanceRoundingRanges)
                item.isAppliedAllCampus = isAppliedAllCampus
                item.campuses = isAppliedAllCampus ? [] : campuses
            }

            return item
        })

        setAttendanceRoundingTables(newAttendanceRoundingTables)
    }

    const onClickSave = useCallback(async () => {
        let errorAttendanceRoundingRanges = validateAttendanceRoundingItem(
            attendanceRoundingTables.reduce((arr, item) => arr.concat(item.attendanceRoundingRanges ?? []), [])
        )
        let errorSpecialAttendanceRoundingRanges = validateAttendanceRoundingItem(specialAttendanceRoundingRanges)
        if (errorAttendanceRoundingRanges || errorSpecialAttendanceRoundingRanges) {
            toastError((errorAttendanceRoundingRanges || errorSpecialAttendanceRoundingRanges) as string)
            return
        }
        try {
            updateGeneralSettings.mutate([
                {
                    field: GeneralSettingField.MakeupDocumentAttachment,
                    checked: enableDocumentAttachment
                },
                {
                    field: GeneralSettingField.MakeupRemote,
                    checked: enableRemoteMakeupAttendance
                },
                {
                    field: GeneralSettingField.EbookLearningRemote,
                    checked: enableEbookLearningRemote
                },
                {
                    field: GeneralSettingField.MakeupDocumentAttachmentMandatory,
                    checked: enableDocumentAttachmentMandatory
                },
                {
                    field: GeneralSettingField.DailySpecialRoundingForCheckin,
                    checked: enableDailySpecialRoundingForCheckin
                },
                {
                    field: GeneralSettingField.DailyAttendanceTimeClockTracking,
                    checked: dailyAttendanceTimeClockTracking
                },
                {
                    field: GeneralSettingField.AttendanceCheckBreakEvent,
                    checked: enableAttendanceCheckBreakEvent
                },
                {
                    field: GeneralSettingField.AccommodatedHoursOption,
                    checked: accommodatedHoursOption
                },
                {
                    field: GeneralSettingField.LateAttendanceApproval,
                    checked: enableLateAttendanceApproval
                },
                {
                    field: GeneralSettingField.LateAttendanceApprovalMinutes,
                    checked:
                        lateAttendanceApprovalMinutes && lateAttendanceApprovalMinutes?.toString()?.length
                            ? lateAttendanceApprovalMinutes.toString()
                            : "0"
                },
                {
                    field: GeneralSettingField.LateAttendanceApprovalMessages,
                    checked: lateAttendanceApprovalMessages?.toString() ?? ""
                },
                {
                    field: GeneralSettingField.RejectionOfDailyAttendanceEnabled,
                    checked: enableRejectionOfDailyAttendanceEnabled
                },
                {
                    field: GeneralSettingField.RejectionOfDailyAttendanceDays,
                    checked:
                        rejectionOfDailyAttendanceDays && rejectionOfDailyAttendanceDays?.toString()?.length
                            ? rejectionOfDailyAttendanceDays.toString()
                            : "0"
                },
                {
                    field: GeneralSettingField.RejectionOfDailyAttendanceMessages,
                    checked: rejectionOfDailyAttendanceMessages?.toString() ?? ""
                },
                {
                    field: GeneralSettingField.LockDailyAttendanceEnabled,
                    checked: enableLockDailyAttendance
                },
                {
                    field: GeneralSettingField.LockDailyAttendanceDays,
                    checked:
                        lockDailyAttendanceDays && lockDailyAttendanceDays?.toString()?.length
                            ? lockDailyAttendanceDays.toString()
                            : "0"
                },
                {
                    field: GeneralSettingField.LockDailyAttendanceMessages,
                    checked: lockDailyAttendanceMessages?.toString() ?? ""
                },
                {
                    field: GeneralSettingField.BaseOnScheduleHourPlusCheckinBufferMinutes,
                    checked:
                        baseOnScheduleHourPlusCheckinBufferMinutes &&
                        baseOnScheduleHourPlusCheckinBufferMinutes?.toString()?.length
                            ? baseOnScheduleHourPlusCheckinBufferMinutes.toString()
                            : "0"
                },
                {
                    field: GeneralSettingField.BaseOnScheduleHourPlusCheckoutBufferMinutes,
                    checked:
                        baseOnScheduleHourPlusCheckoutBufferMinutes &&
                        baseOnScheduleHourPlusCheckoutBufferMinutes?.toString()?.length
                            ? baseOnScheduleHourPlusCheckoutBufferMinutes.toString()
                            : "0"
                },
                {
                    field: GeneralSettingField.BaseOnScheduleCheckinBufferMinutesEnabled,
                    checked: baseOnScheduleCheckinBufferMinutesEnabled
                },
                {
                    field: GeneralSettingField.BaseOnScheduleCheckoutBufferMinutesEnabled,
                    checked: baseOnScheduleCheckoutBufferMinutesEnabled
                },
                {
                    field: GeneralSettingField.DailyAttendanceBreakMinutes,
                    checked:
                        dailyAttendanceBreakMinutes && dailyAttendanceBreakMinutes?.toString()?.length
                            ? dailyAttendanceBreakMinutes.toString()
                            : "0"
                },
                {
                    field: GeneralSettingField.DailyAttendanceBreakOption,
                    checked: dailyAttendanceBreakOption
                }
            ])

            await attendanceRounding.update({
                attendanceRoundingTables: attendanceRoundingTables.map((item) => {
                    const attendanceRoundingRanges = item.attendanceRoundingRanges ?? []
                    const isAppliedAllCampus = !!item.isAppliedAllCampus

                    return {
                        roundingTableId: typeof item.roundingTableId === "number" ? item.roundingTableId : undefined,
                        isAppliedAllCampus: isAppliedAllCampus,
                        campusIds: isAppliedAllCampus ? [] : item.campuses?.map((campus) => campus.id) ?? [],
                        attendanceRoundingRanges: attendanceRoundingRanges.map((item) => ({
                            from: Number(item.from),
                            to: Number(item.to),
                            value: Number(item.value)
                        }))
                    }
                }),
                specialAttendanceRoundingRanges: specialAttendanceRoundingRanges.map((item) => {
                    return {
                        from: Number(item.from),
                        to: Number(item.to),
                        value: Number(item.value)
                    }
                }),
                enabled: isRoundingEnabled,
                type: AttendanceRoundingType.Daily
            })

            toastSuccess("Updated")
        } catch (error: any) {
            handleError(error)
        }
    }, [
        attendanceRoundingTables,
        specialAttendanceRoundingRanges,
        updateGeneralSettings,
        enableDocumentAttachment,
        enableRemoteMakeupAttendance,
        enableEbookLearningRemote,
        enableDocumentAttachmentMandatory,
        enableDailySpecialRoundingForCheckin,
        enableAttendanceCheckBreakEvent,
        enableLateAttendanceApproval,
        lateAttendanceApprovalMinutes,
        lateAttendanceApprovalMessages,
        enableRejectionOfDailyAttendanceEnabled,
        rejectionOfDailyAttendanceDays,
        rejectionOfDailyAttendanceMessages,
        enableLockDailyAttendance,
        lockDailyAttendanceDays,
        lockDailyAttendanceMessages,
        dailyAttendanceTimeClockTracking,
        accommodatedHoursOption,
        attendanceRounding,
        isRoundingEnabled,
        baseOnScheduleHourPlusCheckinBufferMinutes,
        baseOnScheduleHourPlusCheckoutBufferMinutes,
        baseOnScheduleCheckinBufferMinutesEnabled,
        baseOnScheduleCheckoutBufferMinutesEnabled,
        dailyAttendanceBreakOption,
        dailyAttendanceBreakMinutes
    ])

    const onClickCancelBtn = useCallback(() => {
        setAccommodatedHoursOption(settings.accommodatedHoursOption)
        setDailyAttendanceTimeClockTracking(settings.dailyAttendanceTimeClockTracking)
        setDailyAttendanceBreakOption(settings.dailyAttendanceBreakOption)
        setDailyAttendanceBreakMinutes(settings.dailyAttendanceBreakMinutes)
        setEnableDocumentAttachment(settings.isMakeupDocumentAttachmentEnabled)
        setEnableDocumentAttachmentMandatory(settings.isMakeupDocumentAttachmentMandatory)
        setEnableRemoteMakeupAttendance(settings.isRemoteMakeupDocumentEnabled)
        setEnableEbookLearningRemote(settings.isEbookLearningRemoteEnabled)
        setEnableAttendanceCheckBreakEvent(settings.isAttendanceCheckBreakEvent)
        setEnableLateAttendanceApproval(settings.isLateAttendanceApproval)
        setLateAttendanceApprovalMinutes(settings.lateAttendanceApprovalMinutes)
        setLateAttendanceApprovalMessages(settings.lateAttendanceApprovalMessages)
        setEnableRejectionOfDailyAttendanceEnabled(settings.isRejectionOfDailyAttendanceEnabled)
        setRejectionOfDailyAttendanceDays(settings.rejectionOfDailyAttendanceDays)
        setRejectionOfDailyAttendanceMessages(settings.rejectionOfDailyAttendanceMessages)
        setEnableLockDailyAttendance(settings.isLockDailyAttendanceEnabled)
        setLockDailyAttendanceDays(settings.lockDailyAttendanceDays)
        setLockDailyAttendanceMessages(settings.lockDailyAttendanceMessages)
        setIsRoundingEnabled(attendanceRounding.isEnabled)
        setEnableDailySpecialRoundingForCheckin(attendanceRounding.specialAttendanceRoundingEnabled)
        setAttendanceRoundingTables(
            cloneDeep(
                attendanceRounding.attendanceRoundingTables.map((item, index) => ({...item, id: item.roundingTableId}))
            )
        )
        setSpecialAttendanceRoundingRanges(
            cloneDeep(attendanceRounding.specialAttendanceRoundingRanges.map((item, index) => ({...item, id: index})))
        )
        setBaseOnScheduleHourPlusCheckinBufferMinutes(settings.baseOnScheduleHourPlusCheckinBufferMinutes)
        setBaseOnScheduleHourPlusCheckoutBufferMinutes(settings.baseOnScheduleHourPlusCheckoutBufferMinutes)
        setBaseOnScheduleCheckinBufferMinutesEnabled(settings.baseOnScheduleCheckinBufferMinutesEnabled)
        setBaseOnScheduleCheckoutBufferMinutesEnabled(settings.baseOnScheduleCheckoutBufferMinutesEnabled)
    }, [attendanceRounding, settings])

    const isChangedTableData = () => {
        const fieldNames = ["from", "to", "value"]
        const fieldNamesRoundingTable = ["campuses", "isAppliedAllCampus"]

        if (
            attendanceRounding.attendanceRoundingTables.length !== attendanceRoundingTables.length ||
            attendanceRounding.specialAttendanceRoundingRanges.length !== specialAttendanceRoundingRanges.length
        ) {
            return true
        }

        for (let i = 0; i < attendanceRounding.attendanceRoundingTables.length; i++) {
            const attendanceRoundingTable = attendanceRoundingTables[i]
            const attendanceRoundingRanges =
                attendanceRounding.attendanceRoundingTables.find(
                    (table) => table.roundingTableId === attendanceRoundingTable.roundingTableId
                )?.attendanceRoundingRanges ?? []

            if (attendanceRoundingTable.attendanceRoundingRanges.length !== attendanceRoundingRanges.length) {
                return true
            }

            const oldData = pick(attendanceRounding.attendanceRoundingTables[i], fieldNamesRoundingTable)
            const newData = pick(attendanceRoundingTables[i], fieldNamesRoundingTable)

            if (!isEqual(oldData, newData)) {
                return true
            }
        }

        const attendanceRoundingRanges = attendanceRoundingTables.reduce(
            (arr, item) => arr.concat(item.attendanceRoundingRanges ?? []),
            []
        )
        for (let i = 0; i < attendanceRounding.attendanceRoundingRanges.length; i++) {
            const oldData = pick(attendanceRounding.attendanceRoundingRanges[i], fieldNames)
            const newData = pick(attendanceRoundingRanges[i], fieldNames)

            if (!isEqual(oldData, newData)) {
                return true
            }
        }
        for (let i = 0; i < attendanceRounding.specialAttendanceRoundingRanges.length; i++) {
            const oldData = pick(attendanceRounding.specialAttendanceRoundingRanges[i], fieldNames)
            const newData = pick(specialAttendanceRoundingRanges[i], fieldNames)

            if (!isEqual(oldData, newData)) {
                return true
            }
        }
        return false
    }

    const isChanged =
        settings.accommodatedHoursOption !== accommodatedHoursOption ||
        settings.dailyAttendanceTimeClockTracking !== dailyAttendanceTimeClockTracking ||
        settings.isMakeupDocumentAttachmentEnabled !== enableDocumentAttachment ||
        settings.isMakeupDocumentAttachmentMandatory !== enableDocumentAttachmentMandatory ||
        settings.isAttendanceCheckBreakEvent !== enableAttendanceCheckBreakEvent ||
        settings.isLateAttendanceApproval !== enableLateAttendanceApproval ||
        (enableLateAttendanceApproval && settings.lateAttendanceApprovalMinutes !== lateAttendanceApprovalMinutes) ||
        (enableLateAttendanceApproval && settings.lateAttendanceApprovalMessages !== lateAttendanceApprovalMessages) ||
        settings.isRejectionOfDailyAttendanceEnabled !== enableRejectionOfDailyAttendanceEnabled ||
        (enableRejectionOfDailyAttendanceEnabled &&
            settings.rejectionOfDailyAttendanceDays !== rejectionOfDailyAttendanceDays) ||
        (enableRejectionOfDailyAttendanceEnabled &&
            settings.rejectionOfDailyAttendanceMessages !== rejectionOfDailyAttendanceMessages) ||
        settings.isLockDailyAttendanceEnabled !== enableLockDailyAttendance ||
        (enableLockDailyAttendance && settings.lockDailyAttendanceDays !== lockDailyAttendanceDays) ||
        (enableLockDailyAttendance && settings.lockDailyAttendanceMessages !== lockDailyAttendanceMessages) ||
        settings.isRemoteMakeupDocumentEnabled !== enableRemoteMakeupAttendance ||
        settings.isEbookLearningRemoteEnabled !== enableEbookLearningRemote ||
        attendanceRounding.specialAttendanceRoundingEnabled !== enableDailySpecialRoundingForCheckin ||
        isRoundingEnabled !== attendanceRounding.isEnabled ||
        settings.baseOnScheduleHourPlusCheckinBufferMinutes !== baseOnScheduleHourPlusCheckinBufferMinutes ||
        settings.baseOnScheduleHourPlusCheckoutBufferMinutes !== baseOnScheduleHourPlusCheckoutBufferMinutes ||
        settings.baseOnScheduleCheckinBufferMinutesEnabled !== baseOnScheduleCheckinBufferMinutesEnabled ||
        settings.baseOnScheduleCheckoutBufferMinutesEnabled !== baseOnScheduleCheckoutBufferMinutesEnabled ||
        settings.dailyAttendanceBreakOption !== dailyAttendanceBreakOption ||
        settings.dailyAttendanceBreakMinutes !== dailyAttendanceBreakMinutes ||
        isChangedTableData()

    return (
        <div className={styles.wrapper}>
            <span className={styles.title}>Time clock tracking options</span>
            <Col span={24} className={styles.checkboxContainer}>
                <Radio.Group
                    name=""
                    className={styles.radioGroup}
                    onChange={(event) => {
                        setDailyAttendanceTimeClockTracking(event.target.value)
                    }}
                    value={dailyAttendanceTimeClockTracking}>
                    <Radio className={styles.radioItem} value={DailyAttendanceTimeClockTracking.AnyTime}>
                        Any time
                    </Radio>
                    <Radio className={styles.radioItem} value={DailyAttendanceTimeClockTracking.BaseOnScheduleHourOnly}>
                        Base on scheduled hours only
                    </Radio>
                    <Radio className={styles.radioItem} value={DailyAttendanceTimeClockTracking.BaseOnScheduleHourPlus}>
                        Base on scheduled hours plus
                    </Radio>
                </Radio.Group>
            </Col>
            {dailyAttendanceTimeClockTracking === DailyAttendanceTimeClockTracking.BaseOnScheduleHourPlus ? (
                <>
                    <Col span={24} className={styles.checkboxContainer}>
                        <span className={styles.title}>Buffer Time (Minutes)</span>
                    </Col>
                    <Col span={24} className={styles.checkboxContainer}>
                        <div className={styles.lateAttendanceApproval}>
                            <div className={styles.lateAttendanceApprovalItem}>
                                <Checkbox
                                    checked={baseOnScheduleCheckinBufferMinutesEnabled}
                                    onChange={(event) => {
                                        if (!baseOnScheduleCheckoutBufferMinutesEnabled && !event.target.checked) {
                                            setBaseOnScheduleCheckoutBufferMinutesEnabled(true)
                                        }
                                        setBaseOnScheduleCheckinBufferMinutesEnabled(event.target.checked)
                                    }}>
                                    Enable Checkin Buffer Time
                                </Checkbox>
                                {baseOnScheduleCheckinBufferMinutesEnabled ? (
                                    <BaseInput
                                        onChange={(text) => {
                                            setBaseOnScheduleHourPlusCheckinBufferMinutes(text)
                                        }}
                                        placeholder={"Minutes"}
                                        style={{width: 100, marginTop: 10}}
                                        disabledArrow
                                        type="number"
                                        min={0}
                                        value={baseOnScheduleHourPlusCheckinBufferMinutes}
                                    />
                                ) : null}
                            </div>
                            <div className={styles.lateAttendanceApprovalItem}>
                                <Checkbox
                                    checked={baseOnScheduleCheckoutBufferMinutesEnabled}
                                    onChange={(event) => {
                                        if (!baseOnScheduleCheckinBufferMinutesEnabled && !event.target.checked) {
                                            setBaseOnScheduleCheckinBufferMinutesEnabled(true)
                                        }
                                        setBaseOnScheduleCheckoutBufferMinutesEnabled(event.target.checked)
                                    }}>
                                    Enable Checkout Buffer Time
                                </Checkbox>
                                {baseOnScheduleCheckoutBufferMinutesEnabled ? (
                                    <BaseInput
                                        onChange={(text) => {
                                            setBaseOnScheduleHourPlusCheckoutBufferMinutes(text)
                                        }}
                                        placeholder={"Minutes"}
                                        style={{width: 100, marginTop: 10}}
                                        disabledArrow
                                        type="number"
                                        min={0}
                                        value={baseOnScheduleHourPlusCheckoutBufferMinutes}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </Col>
                </>
            ) : null}
            <hr className={styles.line} />
            <span className={styles.title}>Break Event</span>
            <Col span={24} className={styles.checkboxContainer}>
                <Checkbox
                    checked={enableAttendanceCheckBreakEvent}
                    onChange={(event) => setEnableAttendanceCheckBreakEvent(event.target.checked)}>
                    Enable (
                    <span className={styles.hint}>Rounding will be applied to campuses with Rounding Logic</span>)
                </Checkbox>

                {enableAttendanceCheckBreakEvent ? (
                    <div className={styles.breakOption}>
                        <Radio.Group
                            name=""
                            className={styles.radioGroupBreakOption}
                            onChange={(event) => {
                                setDailyAttendanceBreakOption(event.target.value)
                            }}
                            value={dailyAttendanceBreakOption}>
                            <Radio className={styles.radioItem} value={DailyAttendanceBreakOption.AnyTime}>
                                Any time
                            </Radio>
                            <Radio className={styles.radioItem} value={DailyAttendanceBreakOption.BreakMinutes}>
                                Allowable Break
                            </Radio>
                        </Radio.Group>

                        {dailyAttendanceBreakOption === DailyAttendanceBreakOption.BreakMinutes ? (
                            <BaseInput
                                onChange={(text) => {
                                    setDailyAttendanceBreakMinutes(text)
                                }}
                                placeholder={"Minutes"}
                                style={{width: 100}}
                                disabledArrow
                                type="number"
                                min={0}
                                value={dailyAttendanceBreakMinutes}
                            />
                        ) : null}
                    </div>
                ) : null}
            </Col>
            <hr className={styles.line} />
            <span className={styles.title}>Late attendance approval</span>
            <Col span={24} className={styles.checkboxContainer}>
                <Checkbox
                    checked={enableLateAttendanceApproval}
                    onChange={(event) => setEnableLateAttendanceApproval(event.target.checked)}>
                    Enable
                </Checkbox>
            </Col>
            {enableLateAttendanceApproval ? (
                <div className={styles.lateAttendanceApproval}>
                    <div className={styles.lateAttendanceApprovalItem}>
                        <p className={styles.subTitle}>Minutes</p>
                        <BaseInput
                            onChange={(text) => {
                                setLateAttendanceApprovalMinutes(text)
                            }}
                            placeholder={"Minutes"}
                            style={{width: 100}}
                            disabledArrow
                            type="number"
                            min={0}
                            value={lateAttendanceApprovalMinutes}
                        />
                    </div>
                    <div className={styles.lateAttendanceApprovalItem}>
                        <p className={styles.subTitle}>Messages</p>
                        <BaseInput
                            onChange={(text) => {
                                setLateAttendanceApprovalMessages(text)
                            }}
                            placeholder={"Messages"}
                            style={{width: 400}}
                            value={lateAttendanceApprovalMessages}
                        />
                    </div>
                </div>
            ) : null}
            <hr className={styles.line} />
            <span className={styles.title}>Rejection of attendance</span>
            <Col span={24} className={styles.checkboxContainer}>
                <Checkbox
                    checked={enableRejectionOfDailyAttendanceEnabled}
                    onChange={(event) => setEnableRejectionOfDailyAttendanceEnabled(event.target.checked)}>
                    Enable
                </Checkbox>
            </Col>
            {enableRejectionOfDailyAttendanceEnabled ? (
                <div className={styles.lateAttendanceApproval}>
                    <div className={styles.lateAttendanceApprovalItem}>
                        <p className={styles.subTitle}>Days</p>
                        <BaseInput
                            onChange={(text) => {
                                setRejectionOfDailyAttendanceDays(text)
                            }}
                            placeholder={"Days"}
                            style={{width: 100}}
                            disabledArrow
                            type="number"
                            min={0}
                            value={rejectionOfDailyAttendanceDays}
                        />
                    </div>
                    <div className={styles.lateAttendanceApprovalItem}>
                        <p className={styles.subTitle}>Messages</p>
                        <BaseInput
                            onChange={(text) => {
                                setRejectionOfDailyAttendanceMessages(text)
                            }}
                            placeholder={"Messages"}
                            style={{width: 400}}
                            value={rejectionOfDailyAttendanceMessages}
                        />
                    </div>
                </div>
            ) : null}
            <hr className={styles.line} />
            <span className={styles.title}>Lock attendance</span>
            <Col span={24} className={styles.checkboxContainer}>
                <Checkbox
                    checked={enableLockDailyAttendance}
                    onChange={(event) => setEnableLockDailyAttendance(event.target.checked)}>
                    Enable
                </Checkbox>
            </Col>
            {enableLockDailyAttendance ? (
                <div className={styles.lateAttendanceApproval}>
                    <div className={styles.lateAttendanceApprovalItem}>
                        <p className={styles.subTitle}>Days</p>
                        <BaseInput
                            onChange={(text) => {
                                setLockDailyAttendanceDays(text)
                            }}
                            placeholder={"Days"}
                            style={{width: 100}}
                            disabledArrow
                            type="number"
                            min={0}
                            value={lockDailyAttendanceDays}
                        />
                    </div>
                    <div className={styles.lateAttendanceApprovalItem}>
                        <p className={styles.subTitle}>Messages</p>
                        <BaseInput
                            onChange={(text) => {
                                setLockDailyAttendanceMessages(text)
                            }}
                            placeholder={"Messages"}
                            style={{width: 400}}
                            value={lockDailyAttendanceMessages}
                        />
                    </div>
                </div>
            ) : null}
            <hr className={styles.line} />
            <span className={styles.title}>Accommodated hours options</span>
            <Col span={24} className={styles.checkboxContainer}>
                <Radio.Group
                    name=""
                    className={styles.radioGroup}
                    onChange={(event) => {
                        setAccommodatedHoursOption(event.target.value)
                    }}
                    value={accommodatedHoursOption}>
                    <Radio className={styles.radioItem} value={AccommodatedHoursOption.AnyTime}>
                        Any time
                    </Radio>
                    <Radio className={styles.radioItem} value={AccommodatedHoursOption.ByShifts}>
                        By shifts
                    </Radio>
                </Radio.Group>
            </Col>
            <hr className={styles.line} />

            <span className={styles.title}>Make up document attachment</span>
            <Col span={24} className={styles.checkboxContainer}>
                <Checkbox
                    checked={enableDocumentAttachment}
                    onChange={(event) => setEnableDocumentAttachment(event.target.checked)}>
                    Enable
                </Checkbox>
            </Col>
            {enableDocumentAttachment ? (
                <Col span={24} style={{paddingLeft: 20, marginTop: 20}}>
                    <Radio.Group
                        name=""
                        className={styles.radioGroup}
                        onChange={(event) => {
                            setEnableDocumentAttachmentMandatory(event.target.value)
                        }}
                        value={enableDocumentAttachmentMandatory}>
                        <Radio className={styles.radioItem} value={false}>
                            Non Mandatory
                        </Radio>
                        <Radio className={styles.radioItem} value={true}>
                            Mandatory
                        </Radio>
                    </Radio.Group>
                </Col>
            ) : null}
            <div className={styles.separate} />
            <span className={styles.title}>Remote make up attendance</span>
            <Col span={24} className={styles.checkboxContainer}>
                <Checkbox
                    checked={enableRemoteMakeupAttendance}
                    onChange={(event) => setEnableRemoteMakeupAttendance(event.target.checked)}>
                    Enable
                </Checkbox>
            </Col>
            <hr className={styles.separate} />
            <span className={styles.title}>Ebook Learning - Remote Attendance</span>
            <Col span={24} className={styles.checkboxContainer}>
                <Checkbox
                    checked={enableEbookLearningRemote}
                    onChange={(event) => setEnableEbookLearningRemote(event.target.checked)}>
                    Enable
                </Checkbox>
            </Col>
            <hr className={styles.line} />
            <span className={styles.title}>Daily (include make up) attendance rounding</span>
            <Col span={24} className={styles.checkboxContainer}>
                <Radio.Group
                    name=""
                    className={styles.radioGroup}
                    onChange={(event) => {
                        setIsRoundingEnabled(event.target.value === RoundingType.Rounding)
                    }}
                    value={isRoundingEnabled ? RoundingType.Rounding : RoundingType.NoRounding}>
                    <Radio className={styles.radioItem} value={RoundingType.NoRounding}>
                        No Rounding
                    </Radio>
                    <Radio className={styles.radioItem} value={RoundingType.Rounding}>
                        Rounding (Rounding to the minutes not seconds)
                    </Radio>
                </Radio.Group>
                {isRoundingEnabled ? (
                    <>
                        <Col span={24} className={styles.checkboxContainer}>
                            <Checkbox
                                checked={enableDailySpecialRoundingForCheckin}
                                onChange={(event) => setEnableDailySpecialRoundingForCheckin(event.target.checked)}>
                                Special rounding condition for checkin (base on shift start time)
                            </Checkbox>
                        </Col>
                        {enableDailySpecialRoundingForCheckin ? (
                            <RoundingRange
                                isSpecialCondition
                                listData={specialAttendanceRoundingRanges}
                                setListData={setSpecialAttendanceRoundingRanges}
                            />
                        ) : null}
                        <hr className={styles.line} />
                        <span>Rounding logic (Applies to checkin, checkout, break)</span>

                        <RoundingTables
                            attendanceRoundingTables={attendanceRoundingTables}
                            setAttendanceRoundingTable={setAttendanceRoundingTable}
                            addNewAttendanceRoundingTable={addNewAttendanceRoundingTable}
                            removeAttendanceRoundingTable={removeAttendanceRoundingTable}
                        />
                    </>
                ) : null}
            </Col>
            <div className={styles.action}>
                <BaseButton
                    title={t("common:action.cancel")}
                    variant="secondary"
                    className={styles.cancelBtn}
                    disabled={!isChanged}
                    onClick={onClickCancelBtn}
                />
                <BaseButton
                    title={t("common:action.save")}
                    onClick={onClickSave}
                    disabled={!isChanged}
                    loading={attendanceRounding.isUpdating}
                />
            </div>
            <BaseLoading isShow={attendanceRounding.isUpdating || attendanceRounding.isFetching} />
        </div>
    )
}

export {DailyAttendance}
